import { useEffect, useRef, useState } from 'preact/hooks';
import { RecordForm } from './RecordForm';
import { useAuthorityGate } from '../lib/authorityGate';
import { RecordActions } from './RecordActions';
// The capture glyph rides the CaptureGlyph chooser (Clappy on actor_assist,
// the Knot everywhere else) — the alias IS the revert path (720680a).
import { CaptureGlyph as Knot } from './CaptureGlyph';
import {
  ApiError,
  getRecordTypeSchema,
  getRecordWithLinks,
  knotIntent,
  type LinkRow,
  type RecordRow,
  type RecordTypeSchema,
} from '../lib/api';
import { bumpRecordsVersion } from '../lib/recordEditor';
import { useKnotPushToTalk } from '../lib/useKnotPushToTalk';
import './WorkOrderDrawer.css';

// S2 — THE EDITOR DRAWER (wf01 §02b): + Add, because the Knot must never be
// the only door. Everything capture can make, a person can make by hand, on
// a screen built for it — the drawer is the CHROME around the schema-driven
// RecordForm (reused verbatim, never forked): the scoped header, the type
// switcher that changes type WITHOUT closing, and the assist bar — the Knot
// working inside the form.
//
// The seven panes wf01 draws collapse to FIVE here: this pack declares no
// photo record type and no follow-up record type, so those two doors would
// open onto nothing — omitted rather than faked (named in the S2 report).
//
// Delete appears nowhere in this drawer, on purpose (wf01 §02b: "nothing
// here is deleted, it is reversed with a record of it").
//
// ⚑ THAT SENTENCE USED TO END "— edit mode renders RecordForm alone, never
// the generic RecordActions block", and it cost two doors (2026-08-25).
// It was written on 08-10 to keep a DELETE button out. Two days later
// S10 replaced Delete with RETIRE everywhere, so RecordActions stopped
// offering the thing this exclusion existed to prevent — but the exclusion
// stayed, and with it went BOTH the retire door and every lifecycle
// transition on the record this product revolves around. A work order
// created in `intake` could not reach `unassigned`, which is the exact
// state the dispatch board filters on: the core loop, closed by a comment
// about a button that no longer exists.
//
// RecordActions is rendered in edit mode now. It offers transitions and
// Retire — never Delete, which S10 removed at the source.

/** The record types the drawer can CREATE, scoped to this work order — the
 * pack's own vocabulary for wf01's drawer panes (visit · material · note ·
 * receipt · payment). Exported so the spread's + Add can open the first. */
export const CREATE_TYPE_KEYS = [
  'hvac_visit',
  'hvac_material_line',
  'hvac_note',
  'hvac_receipt',
  'hvac_payment',
] as const;

type CreateTypeKey = (typeof CREATE_TYPE_KEYS)[number];

const CREATE_TITLES: Record<CreateTypeKey, string> = {
  hvac_visit: 'Add a visit report',
  hvac_material_line: 'Add a material line',
  hvac_note: 'Add a note',
  hvac_receipt: 'Add a receipt',
  hvac_payment: 'Record a payment',
};

const SWITCHER_LABELS: Record<CreateTypeKey, string> = {
  hvac_visit: 'Visit',
  hvac_material_line: 'Material',
  hvac_note: 'Note',
  hvac_receipt: 'Receipt',
  hvac_payment: 'Payment',
};

export type DrawerRequest =
  | { mode: 'create'; typeKey: CreateTypeKey }
  | { mode: 'edit'; typeKey: string; id: string };

export interface WorkOrderDrawerProps {
  tenantId: string;
  woId: string;
  /** The scoped header line — `WO-4417 · Fortinos #1032`. */
  scopeLabel: string;
  /** The WO's single linked invoice, when exactly one exists — pre-points a
   * new payment at it (its master_detail parent is the invoice). */
  invoiceId?: string;
  request: DrawerRequest | null;
  onSwitch: (request: DrawerRequest) => void;
  onClose: () => void;
}

function isCreateType(key: string): key is CreateTypeKey {
  return (CREATE_TYPE_KEYS as readonly string[]).includes(key);
}

/** Relationship prefill, derived from the pane type's OWN fetched schema —
 * any relationship pointing at the work order pre-points at THIS one; a
 * payment's invoice relationship pre-points at the single linked invoice
 * when there is exactly one. Nothing else is assumed. */
function scopedRels(
  schema: RecordTypeSchema,
  woId: string,
  invoiceId: string | undefined
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const rel of schema.relationships) {
    if (rel.targetType === 'hvac_work_order') out[rel.key] = [woId];
    else if (rel.targetType === 'hvac_invoice' && invoiceId) out[rel.key] = [invoiceId];
  }
  return out;
}

export function WorkOrderDrawer({
  tenantId,
  woId,
  scopeLabel,
  invoiceId,
  request,
  onSwitch,
  onClose,
}: WorkOrderDrawerProps) {
  const [schema, setSchema] = useState<RecordTypeSchema | null>(null);
  const [existing, setExisting] = useState<{ record: RecordRow; links: LinkRow[] } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // ⚑ THE CONVERSION MOVED TO lib/authorityGate.ts (Day 3 walk, item 2). It
  // was written here first and copied nowhere, which is how the generic
  // record form — the door a child actually uses — ended up with no
  // conversion at all. One helper now; this surface keeps its own words.
  const { gate, clearGate, handleSaveError } = useAuthorityGate(tenantId);
  const [assist, setAssist] = useState<{ fields: Record<string, unknown>; nonce: number } | null>(null);
  const [assistHeard, setAssistHeard] = useState<string | null>(null);
  const [assistBusy, setAssistBusy] = useState(false);
  // An assist fill parsed for a DIFFERENT pane rides across the switch and
  // lands once that pane's form is up.
  const pendingFill = useRef<Record<string, unknown> | null>(null);
  const nonceRef = useRef(0);

  const open = request !== null;
  const typeKey = request?.typeKey ?? null;

  useEffect(() => {
    if (!request) return;
    let alive = true;
    setSchema(null);
    setExisting(null);
    setLoadError(null);
    clearGate();
    setAssistHeard(null);
    if (!pendingFill.current) setAssist(null);
    const loads: Promise<void>[] = [
      getRecordTypeSchema(tenantId, request.typeKey).then((s) => {
        if (!alive) return;
        setSchema(s);
        if (pendingFill.current) {
          nonceRef.current += 1;
          setAssist({ fields: pendingFill.current, nonce: nonceRef.current });
          pendingFill.current = null;
        }
      }),
    ];
    if (request.mode === 'edit') {
      loads.push(
        getRecordWithLinks(tenantId, request.id).then((r) => {
          if (alive) setExisting(r);
        })
      );
    }
    Promise.all(loads).catch((e) => {
      if (alive) setLoadError(e instanceof ApiError ? e.message : 'Could not load the form.');
    });
    return () => {
      alive = false;
    };
  }, [tenantId, request]);

  // Escape closes — same convention as every overlay in this app.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // ---- the assist bar: the Knot working inside the form. Speak (hold) or
  // paste — the transcript goes through the SAME intent door a global
  // capture uses, scoped to this work order; whatever it reads fills only
  // fields still empty (RecordForm's assistFill contract — nothing already
  // typed is ever overwritten). ----
  async function handleAssistText(text: string) {
    if (!request) return;
    setAssistBusy(true);
    setAssistHeard(null);
    try {
      const intent = await knotIntent(tenantId, text, { type: 'hvac_work_order', id: woId });
      if (intent.status === 'unclear' || intent.status === 'update-ready' || intent.status === 'update-confirm') {
        // Nothing readable as a create — keep the words visible, verbatim,
        // never dropped. (R-10: an UPDATE spoken into a create form is also
        // kept as words here — this drawer fills forms, it does not edit
        // other records; the Knot's own doors own that flow.)
        setAssistHeard(text);
        return;
      }
      if (intent.targetType !== typeKey && isCreateType(intent.targetType)) {
        // It read as a different pane's record — switch the pane (the drawer
        // stays open) and fill there.
        pendingFill.current = intent.fields;
        onSwitch({ mode: 'create', typeKey: intent.targetType });
        return;
      }
      nonceRef.current += 1;
      setAssist({ fields: intent.fields, nonce: nonceRef.current });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // No intent engine on this instance — the words still survive.
        setAssistHeard(text);
        return;
      }
      setAssistHeard(text);
    } finally {
      setAssistBusy(false);
    }
  }

  const ptt = useKnotPushToTalk(
    tenantId,
    null,
    () => {},
    () => {},
    undefined,
    (transcript) => void handleAssistText(transcript)
  );

  function onAssistPaste(e: ClipboardEvent) {
    const text = e.clipboardData?.getData('text') ?? '';
    if (text.trim().length === 0) return;
    e.preventDefault();
    void handleAssistText(text);
  }

  if (!request) return null;

  const title =
    request.mode === 'create'
      ? CREATE_TITLES[request.typeKey]
      : `Edit ${schema ? schema.label.toLowerCase() : 'record'}${
          existing ? ` — ${existing.record.display_name}` : ''
        }`;

  const formReady = schema !== null && (request.mode === 'create' || existing !== null);

  return (
    <>
      <div class="wo-drawer__scrim" onClick={onClose} />
      <aside class="wo-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div class="wo-drawer__head">
          <span class="wo-drawer__who">
            <span class="wo-drawer__scope">{scopeLabel}</span>
            <span class="wo-drawer__title">{title}</span>
          </span>
          <button type="button" class="wo-drawer__x" onClick={onClose} aria-label="Close">
            Esc ✕
          </button>
        </div>

        <div class="wo-drawer__types" role="group" aria-label="What to add">
          {CREATE_TYPE_KEYS.map((key) => (
            <button
              type="button"
              key={key}
              class="wo-drawer__type"
              aria-pressed={typeKey === key && request.mode === 'create'}
              onClick={() => onSwitch({ mode: 'create', typeKey: key })}
            >
              {SWITCHER_LABELS[key]}
            </button>
          ))}
        </div>

        <div class="wo-drawer__body">
          <div
            class="wo-drawer__assist"
            tabIndex={0}
            onPaste={onAssistPaste}
            data-testid="wo-drawer-assist"
          >
            <button
              type="button"
              class="wo-drawer__assist-knot"
              aria-label="Hold to speak"
              onPointerDown={ptt.onPointerDown}
              onPointerUp={ptt.onPointerUp}
              onPointerCancel={ptt.onPointerCancel}
              onLostPointerCapture={ptt.onLostPointerCapture}
              onClick={ptt.onClick}
            >
              <Knot state={ptt.knotState} amp={ptt.amp} size={26} />
            </button>
            <span class="wo-drawer__assist-tx">
              {assistBusy ? (
                'Listening back…'
              ) : ptt.micDenied ? (
                <>
                  <b>Paste here and it fills what it can.</b> Anything you have already typed stays.
                </>
              ) : (
                <>
                  <b>Say it however you like</b> — anything you have already typed stays.
                </>
              )}
            </span>
          </div>
          {assistHeard ? <div class="wo-drawer__assist-heard">“{assistHeard}”</div> : null}
          {ptt.error ? (
            <div class="wo-drawer__assist-heard" role="status" onClick={ptt.dismissError}>
              {ptt.error}
            </div>
          ) : null}

          {loadError ? (
            <div class="wo-drawer__error" role="alert">
              {loadError}
            </div>
          ) : null}
          {!formReady && !loadError ? <div class="wo-drawer__loading">Loading…</div> : null}

          {formReady && schema ? (
            <>
            <RecordForm
              key={`${request.mode}-${request.typeKey}-${request.mode === 'edit' ? request.id : 'new'}`}
              tenantId={tenantId}
              typeKey={request.typeKey}
              schema={schema}
              existing={request.mode === 'edit' ? (existing ?? undefined) : undefined}
              prefillRels={
                request.mode === 'create' ? scopedRels(schema, woId, invoiceId) : undefined
              }
              assistFill={assist ?? undefined}
              onSaveError={handleSaveError}
              onSaved={() => {
                bumpRecordsVersion();
                onClose();
              }}
              onCancel={onClose}
            />
            {/* Transitions + Retire, on the record being edited. Create mode
                has no record to act on, so it gets none. */}
            {request.mode === 'edit' && existing ? (
              <RecordActions
                tenantId={tenantId}
                schema={schema}
                record={existing.record}
                onChanged={() => {
                  bumpRecordsVersion();
                  onClose();
                }}
                onRetired={() => {
                  bumpRecordsVersion();
                  onClose();
                }}
              />
            ) : null}
            </>
          ) : null}

          {gate ? (
            <div class="wo-drawer__gate" role="status" data-testid="wo-drawer-gate">
              <span class="wo-drawer__gate-stripe" />
              <span>
                {gate.kind === 'filing' ? (
                  <>
                    <b>This one leaves your authority.</b> Converting it into a request…
                  </>
                ) : gate.kind === 'filed' ? (
                  <>
                    <b>This one leaves your authority.</b> It became a request to the office rather
                    than a change you made — the cascade stopped at the boundary and converted.
                  </>
                ) : (
                  <>
                    <b>This one leaves your authority.</b> {gate.message}
                  </>
                )}
              </span>
            </div>
          ) : null}
        </div>
      </aside>
    </>
  );
}
